import { Injectable, NgZone } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';
import { compareIgnoreCase } from './utils';
import { System } from './utils/constants';

export interface UserStatusDto {
  userId: string | null;
  name: string | null;
  status: string;
  lastUpdated: any;
}

@Injectable({ providedIn: 'root' })
export class UserStatusServiceFactory {
  public intervalTime = 420000;
  private hubConnection?: signalR.HubConnection;

  private allStatusesSubject = new BehaviorSubject<UserStatusDto[]>([]);
  private statusUpdateSubject = new BehaviorSubject<UserStatusDto | null>(null);

  public allStatuses$ = this.allStatusesSubject.asObservable();
  public statusUpdate$ = this.statusUpdateSubject.asObservable();

  constructor(private apiService: ApiService) {}

  async startConnection(token: string, tenantId: string, userId: string) {
    // const baseApi = environment.apiEndpoint.replace('/api', '');
    const baseApi = environment.apiEndpoint.replace(/\/api$/, "");

    if (
      !this.hubConnection ||
      this.hubConnection.state === signalR.HubConnectionState.Disconnected
    ) {
      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(
          `${baseApi}/userstatus?tenantId=${tenantId}&userId=${userId}`,
          {
            accessTokenFactory: () => token,
          }
        )
        .withAutomaticReconnect()
        .build();

      this.addListeners();

      try {
        await this.hubConnection.start();
        console.log('[SignalR] Connected to UserStatusHub');
      } catch (err) {
        console.error('[SignalR] Connection failed:', err);
      }
    } else {
      console.log('[SignalR] Already connected or connecting.');
    }
  }

  async stopConnection() {
    await this.hubConnection?.stop();
    console.log('[SignalR] Disconnected');
  }

  private addListeners() {
    this.hubConnection?.off('ReceiveAllStatuses');
    this.hubConnection?.off('ReceiveStatusUpdate');

    this.hubConnection?.on(
      'ReceiveAllStatuses',
      (statuses: UserStatusDto[]) => {
        this.allStatusesSubject.next(statuses);
      }
    );

    this.hubConnection?.on('ReceiveStatusUpdate', (status: UserStatusDto) => {
      this.statusUpdateSubject.next(status);

      const current = this.allStatusesSubject.value;
      const index = current.findIndex((u) => u.userId === status.userId);

      if (index >= 0) {
        current[index] = status;
      } else {
        current.push(status);
      }

      this.allStatusesSubject.next([...current]);
    });
  }

  listenToStatuses(tenantId: string): Observable<UserStatusDto[]> {
    return this.allStatuses$;
  }

  listenToUserStatus(
    tenantId: string,
    userId: string
  ): Observable<UserStatusDto | null> {
    return new Observable((subscriber) => {
      const sub = this.statusUpdate$.subscribe((status) => {
        if (compareIgnoreCase(status?.userId ?? '', userId)) {
          subscriber.next(status);
        }
      });
      return () => sub.unsubscribe();
    });
  }

  listenToMyStatus(
    tenantId: string,
    userId: string
  ): Observable<UserStatusDto | null> {
    return this.listenToUserStatus(tenantId, userId);
  }

  getUserStatusesChunked(tenantId: string, userIds: string[]) {
    return this.apiService.get(
      `/tenants/${tenantId}/UserStatuses?isNoLoading=true`
    );
  }

  getDefaultUserStatusDto(userId: string) {
    return {
      avatarUrl: null,
      lastUpdated: null,
      name: null,
      status: System.UserStatusClientEvent.Idle,
      userId: userId,
    } as UserStatusDto;
  }

  getUserStatus(tenantId: string, userId: string) {
    return this.apiService.get(
      `/tenants/${tenantId}/UserStatuses/${userId}?isNoLoading=true`
    );
  }

  updateUserStatus(tenantId: string, status: UserStatusDto) {
    return this.apiService.post(
      `/tenants/${tenantId}/UserStatuses?isNoLoading=true`,
      status
    );
  }

  sendHeartbeat(tenantId: string): Observable<any> {
    return this.apiService.post(
      `/tenants/${tenantId}/UserStatuses/heartbeat?isNoLoading=true`
    );
  }
}
